import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Bot,
  Building2,
  Check,
  FileText,
  FolderOpen,
  Headset,
  MessageSquare,
  PenTool,
  Sparkles,
  Users,
} from "lucide-react";

import { brand } from "../theme/theme";
import { Button } from "../components/Button";
import { useSidebar } from "../components/Sidebar";
import { canUseSupport } from "../utils/supportTickets";

// Encima del header (1002) y del drawer móvil (1200), por debajo de los modales (100000)
const Z_INDEX = 1500;
const SPOT_PADDING = 6;
const GAP = 16;
const EDGE = 16;
const DESKTOP_CARD_WIDTH = 368;
const MISSING_TARGET_TIMEOUT = 1500;

const STEPS = [
  {
    target: "clients",
    inSidebar: true,
    icon: Users,
    title: "Clientes",
    description:
      "Tu cartera en un solo sitio. Cada cliente tiene su ficha con sus datos, el acceso a su portal y todo lo que trabajas con él.",
    tags: ["Ficha de cliente", "Portal del cliente", "Integraciones"],
  },
  {
    target: "analytics",
    inSidebar: true,
    icon: BarChart3,
    title: "Analíticas",
    description:
      "Conecta las fuentes de datos de tus clientes y sigue su rendimiento sin salir de Markezy.",
    tags: ["Google Analytics", "PageSpeed", "Datos SEO"],
  },
  {
    target: "projects",
    inSidebar: true,
    icon: FolderOpen,
    title: "Proyectos",
    description:
      "Organiza el trabajo de cada cliente en fases y tareas, con responsables y fechas. Usa plantillas para no empezar de cero.",
    tags: ["Fases y tareas", "Plantillas", "Seguimiento"],
  },
  {
    target: "conversations",
    inSidebar: true,
    icon: MessageSquare,
    title: "Conversaciones",
    description:
      "Habla con tus clientes desde la plataforma, con hilos por tema, menciones y archivos adjuntos.",
    tags: ["Hilos", "Menciones", "Adjuntos"],
  },
  {
    target: "contents",
    inSidebar: true,
    icon: PenTool,
    title: "Contenidos",
    description:
      "Genera contenidos con IA adaptados a cada cliente y a cada canal, y organiza su publicación.",
    tags: ["Generación con IA", "Por canal", "Programación"],
  },
  {
    target: "reports",
    inSidebar: true,
    icon: FileText,
    title: "Informes",
    description:
      "Crea informes para tus clientes a partir de sus datos y tenlos listos para compartir en minutos.",
    tags: ["Datos reales", "Listos para enviar"],
  },
  {
    target: "agency",
    inSidebar: true,
    icon: Building2,
    title: "Panel de agencia",
    description:
      "La parte interna de tu agencia: gestiona tu equipo y comparte con él todo lo que el cliente no ve.",
    tags: ["Equipo", "Conversaciones internas", "Documentación interna", "Enlaces"],
  },
  {
    target: "copilot",
    inSidebar: false,
    icon: Bot,
    title: "Copiloto",
    description:
      "Tu asistente con IA. Tiene acceso a los clientes de la plataforma, así que puedes preguntarle:",
    bullets: [
      "Cualquier cosa de tus clientes: proyectos, tareas, conversaciones…",
      "Dudas sobre cómo usar la plataforma.",
      "Recomendaciones de marketing y SEO: metodología, estrategia y buenas prácticas.",
    ],
  },
  {
    target: "support",
    inSidebar: false,
    icon: Headset,
    title: "Soporte",
    description:
      "¿Tienes dudas sobre cómo usar la plataforma? Escríbenos desde aquí y te ayudamos.",
    isAvailable: canUseSupport,
  },
];

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, .55); }
  70% { box-shadow: 0 0 0 12px rgba(255, 255, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
`;

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(.98); }
  to { opacity: 1; transform: none; }
`;

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

const getTargetRect = (target) => {
  const nodes = Array.from(document.querySelectorAll(`[data-welcome-tour="${target}"]`))
    .map((node) => node.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0);
  if (!nodes.length) return null;

  const top = Math.min(...nodes.map((rect) => rect.top));
  const left = Math.min(...nodes.map((rect) => rect.left));
  const bottom = Math.max(...nodes.map((rect) => rect.bottom));
  const right = Math.max(...nodes.map((rect) => rect.right));
  return { top, left, bottom, right, width: right - left, height: bottom - top };
};

const sameRect = (a, b) =>
  a === b ||
  (a && b && ["top", "left", "width", "height"].every((key) => Math.abs(a[key] - b[key]) < 0.5));

// Coloca la tarjeta junto al elemento resaltado y devuelve dónde apunta la flecha
const computeLayout = (rect, card, step, isMobile) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = isMobile ? Math.min(vw - EDGE * 2, 440) : DESKTOP_CARD_WIDTH;
  const height = card?.height || 260;

  if (!rect) {
    return { width, top: (vh - height) / 2, left: (vw - width) / 2, arrow: null };
  }

  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;

  if (!isMobile && step.inSidebar) {
    const top = clamp(centerY - height / 2, 72, vh - height - EDGE);
    return {
      width,
      top,
      left: rect.right + SPOT_PADDING + GAP,
      arrow: { side: "left", offset: clamp(centerY - top, 24, height - 24) },
    };
  }

  const left = isMobile ? (vw - width) / 2 : clamp(rect.right - width, EDGE, vw - width - EDGE);
  const spaceBelow = vh - rect.bottom;
  const below = !step.inSidebar || spaceBelow >= height + GAP * 2 || spaceBelow > rect.top;
  const top = below
    ? clamp(rect.bottom + SPOT_PADDING + GAP, EDGE, vh - height - EDGE)
    : clamp(rect.top - SPOT_PADDING - GAP - height, EDGE, vh - height - EDGE);
  return {
    width,
    top,
    left,
    arrow: { side: below ? "top" : "bottom", offset: clamp(centerX - left, 24, width - 24) },
  };
};

export function WelcomeSidebarTour({ user, onFinish }) {
  const theme = useTheme();
  const { isMobile, open, setOpen, setOpenMobile, setMobileLocked } = useSidebar();
  const steps = useMemo(
    () => STEPS.filter((step) => !step.isAvailable || step.isAvailable(user)),
    [user]
  );
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardSize, setCardSize] = useState(null);
  const rootRef = useRef(null);
  const cardRef = useRef(null);
  const nextButtonRef = useRef(null);
  const directionRef = useRef(1);
  const sidebarWasOpenRef = useRef(open);
  const step = steps[index];
  const isLast = index === steps.length - 1;

  const goTo = useCallback(
    (nextIndex) => {
      if (nextIndex >= steps.length) {
        onFinish();
        return;
      }
      directionRef.current = nextIndex >= index ? 1 : -1;
      setIndex(clamp(nextIndex, 0, steps.length - 1));
    },
    [index, onFinish, steps.length]
  );

  // Desktop: expande el sidebar comprimido durante el recorrido y lo restaura al terminar.
  // Móvil: fija el drawer para que no se cierre con el backdrop o Escape.
  useEffect(() => {
    const sidebarWasOpen = sidebarWasOpenRef.current;
    setMobileLocked(true);
    if (!sidebarWasOpen) setOpen(true);

    return () => {
      setMobileLocked(false);
      setOpenMobile(false);
      if (!sidebarWasOpen) setOpen(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!step) return undefined;
    if (isMobile) setOpenMobile(step.inSidebar);

    // Espera a que el drawer o el sidebar terminen de abrirse antes de hacer scroll
    const scrollTimer = window.setTimeout(() => {
      const nodes = document.querySelectorAll(`[data-welcome-tour="${step.target}"]`);
      nodes[nodes.length - 1]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      nodes[0]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, isMobile ? 260 : 220);

    // Si el elemento no existe (permisos, plan…), se salta el paso
    const missingTimer = window.setTimeout(() => {
      if (getTargetRect(step.target)) return;
      const nextIndex = index + directionRef.current;
      if (nextIndex < 0) return;
      goTo(nextIndex);
    }, MISSING_TARGET_TIMEOUT);

    nextButtonRef.current?.focus({ preventScroll: true });

    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(missingTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, isMobile]);

  // Sigue al elemento mientras se anima el sidebar, se hace scroll o cambia el viewport
  useLayoutEffect(() => {
    if (!step) return undefined;
    let frame;
    let lastRect = null;
    let lastCard = null;

    const track = () => {
      const nextRect = getTargetRect(step.target);
      if (!sameRect(nextRect, lastRect)) {
        lastRect = nextRect;
        setRect(nextRect);
      }
      const node = cardRef.current;
      if (node) {
        const nextCard = { width: node.offsetWidth, height: node.offsetHeight };
        if (!lastCard || lastCard.height !== nextCard.height || lastCard.width !== nextCard.width) {
          lastCard = nextCard;
          setCardSize(nextCard);
        }
      }
      frame = window.requestAnimationFrame(track);
    };

    track();
    return () => window.cancelAnimationFrame(frame);
  }, [step]);

  // Bloquea el scroll de fondo sin tocar el overflow del body: el drawer de MUI también lo
  // guarda y restaura, y pisarse dejaba la página sin scroll al terminar
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const blockScroll = (event) => {
      if (!cardRef.current?.contains(event.target)) event.preventDefault();
    };
    root.addEventListener("wheel", blockScroll, { passive: false });
    root.addEventListener("touchmove", blockScroll, { passive: false });
    return () => {
      root.removeEventListener("wheel", blockScroll);
      root.removeEventListener("touchmove", blockScroll);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
      } else if (event.key === "ArrowRight") {
        goTo(index + 1);
      } else if (event.key === "ArrowLeft" && index > 0) {
        goTo(index - 1);
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [goTo, index]);

  if (!step) return null;

  const layout = computeLayout(rect, cardSize, step, isMobile);
  const Icon = step.icon;

  return createPortal(
    <Box
      ref={rootRef}
      data-tutorial-active="true"
      data-tutorial-surface="welcome-sidebar-tour"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-tour-title"
      aria-describedby="welcome-tour-description"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: Z_INDEX,
        "@media (prefers-reduced-motion: reduce)": {
          "& *": { animation: "none !important", transition: "none !important" },
        },
      }}
    >
      {/* Capa que bloquea la interacción con la app: el recorrido solo avanza con sus botones */}
      <Box sx={{ position: "absolute", inset: 0, touchAction: "none" }} />

      <Box
        aria-hidden="true"
        sx={{
          position: "fixed",
          pointerEvents: "none",
          borderRadius: "14px",
          transition: "top 320ms cubic-bezier(.2,.8,.2,1), left 320ms cubic-bezier(.2,.8,.2,1), width 320ms cubic-bezier(.2,.8,.2,1), height 320ms cubic-bezier(.2,.8,.2,1)",
          boxShadow: "0 0 0 9999px rgba(15, 23, 42, .64)",
          ...(rect
            ? {
              top: rect.top - SPOT_PADDING,
              left: rect.left - SPOT_PADDING,
              width: rect.width + SPOT_PADDING * 2,
              height: rect.height + SPOT_PADDING * 2,
            }
            : { top: "50%", left: "50%", width: 0, height: 0 }),
          "&::after": rect
            ? {
              content: '""',
              position: "absolute",
              inset: 0,
              borderRadius: "inherit",
              border: "2px solid rgba(255, 255, 255, .95)",
              animation: `${pulse} 1.8s ease-out infinite`,
            }
            : undefined,
        }}
      />

      <Box
        ref={cardRef}
        sx={{
          position: "fixed",
          top: layout.top,
          left: layout.left,
          width: layout.width,
          maxHeight: `calc(100dvh - ${EDGE * 2}px)`,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          borderRadius: "18px",
          boxShadow: "0 28px 70px rgba(15, 23, 42, .32)",
          transition: "top 320ms cubic-bezier(.2,.8,.2,1), left 320ms cubic-bezier(.2,.8,.2,1)",
          boxSizing: "border-box",
        }}
      >
        {layout.arrow && (
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              width: 14,
              height: 14,
              bgcolor: "background.paper",
              transform: "rotate(45deg)",
              transition: "top 320ms ease, left 320ms ease",
              ...(layout.arrow.side === "left" && { left: -7, top: layout.arrow.offset - 7 }),
              ...(layout.arrow.side === "top" && { top: -7, left: layout.arrow.offset - 7 }),
              ...(layout.arrow.side === "bottom" && { bottom: -7, left: layout.arrow.offset - 7 }),
            }}
          />
        )}

        <Box
          key={step.target}
          sx={{
            position: "relative",
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
            p: { xs: 2.25, sm: 2.75 },
            pb: { xs: 2, sm: 2.25 },
            animation: `${cardIn} 280ms cubic-bezier(.2,.8,.2,1)`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                borderRadius: "13px",
                color: "common.white",
                background: brand.gradientDiagonal,
                boxShadow: brand.shadow,
              }}
            >
              <Icon size={21} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: brand.softText,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                }}
              >
                Recorrido por Markezy
              </Typography>
              <Typography
                id="welcome-tour-title"
                component="h2"
                sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.2, letterSpacing: "-.02em" }}
              >
                {step.title}
              </Typography>
            </Box>
            {/* Mismo contador que TutorialJoyrideTooltip */}
            <Box
              aria-label={`Paso ${index + 1} de ${steps.length}`}
              sx={{
                flexShrink: 0,
                alignSelf: "flex-start",
                ml: "auto",
                px: 1.5,
                py: 0.75,
                borderRadius: "10px",
                border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.text.primary,
                fontSize: "0.82rem",
                fontWeight: 700,
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              {index + 1} / {steps.length}
            </Box>
          </Box>

          <Typography
            id="welcome-tour-description"
            variant="body2"
            sx={{ color: "text.secondary", lineHeight: 1.6 }}
          >
            {step.description}
          </Typography>

          {step.tags && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.75 }}>
              {step.tags.map((tag) => (
                <Box
                  key={tag}
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.1,
                    py: 0.45,
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    color: brand.softText,
                    bgcolor: brand.soft,
                    border: `1px solid ${brand.softBorder}`,
                  }}
                >
                  <Sparkles size={11} /> {tag}
                </Box>
              ))}
            </Box>
          )}

          {step.bullets && (
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mt: 1.5, display: "grid", gap: 1 }}>
              {step.bullets.map((bullet) => (
                <Box component="li" key={bullet} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      mt: "1px",
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: "50%",
                      color: brand.softText,
                      bgcolor: brand.soft,
                    }}
                  >
                    <Check size={12} strokeWidth={3} />
                  </Box>
                  <Typography variant="body2" sx={{ lineHeight: 1.5, fontWeight: 500 }}>
                    {bullet}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            px: { xs: 2.25, sm: 2.75 },
            py: 1.75,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", gap: 0.5, minWidth: 0, overflow: "hidden" }} aria-hidden="true">
            {steps.map((item, dotIndex) => (
              <Box
                key={item.target}
                sx={{
                  flexShrink: 0,
                  width: dotIndex === index ? 18 : 6,
                  height: 6,
                  borderRadius: 999,
                  background: dotIndex <= index ? brand.gradient : "rgba(15, 23, 42, .12)",
                  transition: "width 240ms ease",
                }}
              />
            ))}
          </Box>
          <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
            {index > 0 && (
              <Button
                variant="ghost"
                size="sm"
                aria-label="Paso anterior"
                onClick={() => goTo(index - 1)}
                sx={{ px: 1.25, minWidth: 0 }}
              >
                <ArrowLeft size={16} />
              </Button>
            )}
            <Button
              ref={nextButtonRef}
              size="sm"
              onClick={() => goTo(index + 1)}
              sx={{ whiteSpace: "nowrap" }}
            >
              {isLast ? (isMobile ? "Terminar" : "Ver primeros pasos") : "Siguiente"} <ArrowRight size={15} />
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>,
    document.body
  );
}

WelcomeSidebarTour.propTypes = {
  user: PropTypes.object,
  onFinish: PropTypes.func.isRequired,
};
